import React from 'react';
import {
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useConfirm } from 'material-ui-confirm';
import { ChatMessage } from './types';

interface ChatHeaderProps {
  messages: ChatMessage[];
  onClearChat: () => void | Promise<void>;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ messages, onClearChat }) => {
  const confirm = useConfirm();

  const handleClearChat = async () => {
    try {
      await confirm({
        title: 'Clear Chat History?',
        description:
          'Are you sure you want to clear all messages and delete this conversation from the database? This action cannot be undone.',
        confirmationText: 'Clear & Delete',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      await onClearChat();
    } catch {
      // User cancelled
    }
  };

  const userMessageCount = messages.filter((m) => m.role === 'user').length;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 1.5,
        py: 0.75,
        borderBottom: '1px solid #f1f5f9',
        bgcolor: '#ffffff',
        flexShrink: 0,
        minHeight: 38,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e293b' }}>
          Chat
        </Typography>
        {messages.length > 0 && (
          <Chip
            label={`${userMessageCount} message${userMessageCount === 1 ? '' : 's'}`}
            size="small"
            sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600 }}
          />
        )}
      </Stack>

      {messages.length > 0 && (
        <Tooltip title="Clear chat history">
          <IconButton
            size="small"
            onClick={handleClearChat}
            sx={{
              p: 0.5,
              color: '#94a3b8',
              '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
